import React, { useMemo } from "react";
import { differenceInDays, parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, CalendarX, Package, CheckCircle } from "lucide-react";

export default function AlertasValidade({ insumos, onEdit }) {
  const hoje = new Date();

  const insumosComValidade = useMemo(() => {
    return insumos
      .filter(i => i.validade)
      .map(i => {
        const dataValidade = parseISO(i.validade);
        const diasRestantes = differenceInDays(dataValidade, hoje);
        let status = "ok";
        if (diasRestantes < 0) status = "vencido";
        else if (diasRestantes <= 7) status = "critico";
        else if (diasRestantes <= 30) status = "alerta";
        else if (diasRestantes <= 90) status = "atencao";
        return { ...i, diasRestantes, status, dataValidade };
      })
      .filter(i => i.status !== "ok")
      .sort((a, b) => a.diasRestantes - b.diasRestantes);
  }, [insumos, hoje]);

  const vencidos = insumosComValidade.filter(i => i.status === "vencido");
  const criticos = insumosComValidade.filter(i => i.status === "critico");
  const alerta = insumosComValidade.filter(i => i.status === "alerta");
  const atencao = insumosComValidade.filter(i => i.status === "atencao");

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const statusConfig = {
    vencido: { bg: "bg-red-100 border-red-300", text: "text-red-700", badge: "bg-red-600 text-white", label: "VENCIDO", icon: CalendarX },
    critico: { bg: "bg-orange-100 border-orange-300", text: "text-orange-700", badge: "bg-orange-500 text-white", label: "Vence em breve", icon: AlertTriangle },
    alerta: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", badge: "bg-amber-500 text-white", label: "30 dias", icon: Clock },
    atencao: { bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-700", badge: "bg-yellow-500 text-white", label: "90 dias", icon: Clock }
  };

  const renderItem = (item) => {
    const config = statusConfig[item.status];
    const Icon = config.icon;
    return (
      <div
        key={item.id}
        className={`p-4 rounded-xl border ${config.bg} flex items-center gap-4`}
      >
        <div className={`p-2 rounded-lg ${item.status === 'vencido' ? 'bg-red-200' : 'bg-white/50'}`}>
          <Icon className={`w-5 h-5 ${config.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-stone-800">{item.nome}</p>
            <Badge className={config.badge}>{config.label}</Badge>
            {item.lote && <Badge variant="outline" className="text-xs">Lote: {item.lote}</Badge>}
          </div>
          <p className="text-sm text-stone-600 mt-1">
            {item.status === 'vencido' 
              ? `Venceu há ${Math.abs(item.diasRestantes)} dias (${format(item.dataValidade, "dd/MM/yyyy")})`
              : `Vence em ${item.diasRestantes} dias (${format(item.dataValidade, "dd/MM/yyyy")})`
            }
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Estoque: {item.quantidade_estoque} {item.unidade} • Valor: {formatCurrency((item.quantidade_estoque || 0) * (item.custo_unitario || 0))}
          </p>
        </div>
        {onEdit && (
          <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
            Ver
          </Button>
        )}
      </div>
    );
  };

  if (insumosComValidade.length === 0) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-full">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-green-800">Tudo em ordem!</h3>
            <p className="text-green-700 text-sm">Nenhum item próximo do vencimento ou vencido.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={vencidos.length > 0 ? "bg-red-100 border-red-300" : "bg-stone-50"}>
          <CardContent className="p-4 text-center">
            <CalendarX className={`w-6 h-6 mx-auto mb-1 ${vencidos.length > 0 ? "text-red-600" : "text-stone-400"}`} />
            <p className="text-2xl font-bold">{vencidos.length}</p>
            <p className="text-xs text-stone-600">Vencidos</p>
          </CardContent>
        </Card>
        <Card className={criticos.length > 0 ? "bg-orange-100 border-orange-300" : "bg-stone-50"}>
          <CardContent className="p-4 text-center">
            <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${criticos.length > 0 ? "text-orange-600" : "text-stone-400"}`} />
            <p className="text-2xl font-bold">{criticos.length}</p>
            <p className="text-xs text-stone-600">Até 7 dias</p>
          </CardContent>
        </Card>
        <Card className={alerta.length > 0 ? "bg-amber-100 border-amber-300" : "bg-stone-50"}>
          <CardContent className="p-4 text-center">
            <Clock className={`w-6 h-6 mx-auto mb-1 ${alerta.length > 0 ? "text-amber-600" : "text-stone-400"}`} />
            <p className="text-2xl font-bold">{alerta.length}</p>
            <p className="text-xs text-stone-600">Até 30 dias</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-1 text-yellow-600" />
            <p className="text-2xl font-bold">{atencao.length}</p>
            <p className="text-xs text-stone-600">Até 90 dias</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Alertas */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Alertas de Validade
            <Badge className="bg-amber-100 text-amber-700">{insumosComValidade.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {vencidos.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-red-700 flex items-center gap-1">
                <CalendarX className="w-4 h-4" /> Produtos Vencidos
              </p>
              {vencidos.map(renderItem)}
            </div>
          )}
          {criticos.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-orange-700 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Vencem em até 7 dias
              </p>
              {criticos.map(renderItem)}
            </div>
          )}
          {alerta.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-amber-700 flex items-center gap-1">
                <Clock className="w-4 h-4" /> Vencem em até 30 dias
              </p>
              {alerta.map(renderItem)}
            </div>
          )}
          {atencao.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-yellow-700 flex items-center gap-1">
                <Clock className="w-4 h-4" /> Vencem em até 90 dias
              </p>
              {atencao.map(renderItem)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}